export interface PdfPageText {
  pageNumber: number
  itemTexts: string[]
}

export interface PdfItemRange {
  start: number
  end: number
}

export type PdfMarkRanges = Map<number, Map<number, PdfItemRange[]>>

export interface PdfTextMatch {
  pageNumber: number
  marks: PdfMarkRanges
}

export interface PdfTextSource {
  numPages: number
  getPage(pageNumber: number): Promise<{ getTextContent(): Promise<{ items: ReadonlyArray<object> }> }>
}

interface PdfTextPosition {
  pageNumber: number
  itemIndex: number
  charIndex: number
}

interface IndexedPdfText {
  text: string
  positions: PdfTextPosition[]
}

const PASSAGE_RUN_CHARS = 40
const CLOSING_RUN_CHARS = [40, 20, 10]
const PASSAGE_STRETCH = 2
const IGNORED_IN_PASSAGES = /[\s­‐-―−-]/
const COMPARABLE_QUOTES: Record<string, string> = {
  "‘": "'",
  "’": "'",
  "‚": "'",
  "‛": "'",
  "′": "'",
  "“": '"',
  "”": '"',
  "„": '"',
  "‟": '"',
  "″": '"',
}
const HTML_ESCAPES: Record<string, string> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }

export async function pdfPageTexts(pdf: PdfTextSource): Promise<PdfPageText[]> {
  const pages: PdfPageText[] = []
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const content = await (await pdf.getPage(pageNumber)).getTextContent()
    pages.push({ pageNumber, itemTexts: content.items.map((item) => ("str" in item && typeof item.str === "string" ? item.str : "")) })
  }
  return pages
}

function lowerCased(char: string): string {
  return char.toLowerCase()
}

function comparableInPassages(char: string): string {
  if (IGNORED_IN_PASSAGES.test(char)) return ""
  return COMPARABLE_QUOTES[char] ?? char.toLowerCase()
}

function normalized(text: string, normalizedChar: (char: string) => string): string {
  return Array.from(text, normalizedChar).join("")
}

function indexedPdfText(pages: PdfPageText[], normalizedChar: (char: string) => string): IndexedPdfText {
  const textParts: string[] = []
  const positions: PdfTextPosition[] = []
  for (const page of pages) {
    page.itemTexts.forEach((itemText, itemIndex) => {
      for (let charIndex = 0; charIndex < itemText.length; charIndex++) {
        const kept = normalizedChar(itemText[charIndex])
        textParts.push(kept)
        for (let keptIndex = 0; keptIndex < kept.length; keptIndex++) positions.push({ pageNumber: page.pageNumber, itemIndex, charIndex })
      }
    })
  }
  return { text: textParts.join(""), positions }
}

function matchBetween(indexed: IndexedPdfText, start: number, end: number): PdfTextMatch {
  const marks: PdfMarkRanges = new Map()
  for (const position of indexed.positions.slice(start, end)) {
    const pageMarks = marks.get(position.pageNumber) ?? new Map<number, PdfItemRange[]>()
    marks.set(position.pageNumber, pageMarks)
    const [itemRange] = pageMarks.get(position.itemIndex) ?? []
    pageMarks.set(position.itemIndex, [
      itemRange ? { start: Math.min(itemRange.start, position.charIndex), end: Math.max(itemRange.end, position.charIndex + 1) } : { start: position.charIndex, end: position.charIndex + 1 },
    ])
  }
  return { pageNumber: indexed.positions[start].pageNumber, marks }
}

export function pdfTextMatches(pages: PdfPageText[], query: string): PdfTextMatch[] {
  const needle = normalized(query, lowerCased)
  if (!needle) return []
  return pages.flatMap((page) => {
    const indexed = indexedPdfText([page], lowerCased)
    const matches: PdfTextMatch[] = []
    for (let at = indexed.text.indexOf(needle); at !== -1; at = indexed.text.indexOf(needle, at + needle.length)) {
      matches.push(matchBetween(indexed, at, at + needle.length))
    }
    return matches
  })
}

export function citedPassageMatch(pages: PdfPageText[], citedText: string): PdfTextMatch | null {
  const needle = normalized(citedText, comparableInPassages)
  if (!needle) return null
  const indexed = indexedPdfText(pages, comparableInPassages)
  const whole = indexed.text.indexOf(needle)
  if (whole !== -1) return matchBetween(indexed, whole, whole + needle.length)
  const opening = needle.slice(0, PASSAGE_RUN_CHARS)
  const start = indexed.text.indexOf(opening)
  if (start === -1) return null
  const end = passageEnd(indexed.text, needle, start, start + opening.length) ?? Math.min(indexed.text.length, start + needle.length)
  return matchBetween(indexed, start, end)
}

function passageEnd(text: string, needle: string, start: number, searchFrom: number): number | null {
  for (const runChars of CLOSING_RUN_CHARS) {
    const closing = needle.slice(-runChars)
    const closingAt = text.indexOf(closing, searchFrom)
    const closingEnd = closingAt + closing.length
    if (closingAt !== -1 && closingEnd - start <= needle.length * PASSAGE_STRETCH) return closingEnd
  }
  return null
}

export function mergedMarks(matches: PdfTextMatch[]): PdfMarkRanges {
  const merged: PdfMarkRanges = new Map()
  for (const match of matches) {
    for (const [pageNumber, pageMarks] of match.marks) {
      const mergedPage = merged.get(pageNumber) ?? new Map<number, PdfItemRange[]>()
      merged.set(pageNumber, mergedPage)
      for (const [itemIndex, ranges] of pageMarks) mergedPage.set(itemIndex, [...(mergedPage.get(itemIndex) ?? []), ...ranges])
    }
  }
  return merged
}

function escapedHtml(text: string): string {
  return text.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char])
}

export function markedItemHtml(itemText: string, ranges: PdfItemRange[] | undefined, markOpeningTag: string): string {
  if (!ranges?.length) return escapedHtml(itemText)
  let html = ""
  let cursor = 0
  for (const { start, end } of [...ranges].sort((left, right) => left.start - right.start)) {
    html += escapedHtml(itemText.slice(cursor, start)) + markOpeningTag + escapedHtml(itemText.slice(start, end)) + "</mark>"
    cursor = end
  }
  return html + escapedHtml(itemText.slice(cursor))
}
